import t from 'babel-plugin-yate/macro';
import {isValid, parse, differenceInYears} from 'date-fns';

import {parseDate} from './misc';
import isValidIBAN from './ibanValidator';

export const composeFormValidators =
  (...validators) =>
  value =>
    validators.reduce(
      (error, validator) => error || validator(value),
      undefined,
    );

export const hasItems = message => {
  const errorMessage = message || t`At least one option is required`;

  return value => {
    if (Array.isArray(value) && value.length !== 0) {
      return undefined;
    }

    return errorMessage;
  };
};

const errorHelper = ({value, errorMessage, nameRegex}) => {
  // Undefined is valid
  if (['undefined', 'object'].includes(typeof value)) {
    return undefined;
  }

  // Return error message if value is not a string
  if (typeof value !== 'string') {
    return errorMessage;
  }

  if (nameRegex.test(value)) {
    return undefined;
  }

  return errorMessage;
};

export const isRequired = (val, message) => {
  const errorMessage = message || t`This field is required`;

  return value => {
    if (typeof value === val) {
      return undefined;
    }

    if (typeof value === 'number') {
      return undefined;
    }

    if (
      typeof value === 'string' &&
      value &&
      !!value.replace(/\s/g, '').length
    ) {
      return undefined;
    }

    if (typeof value === 'boolean') {
      return undefined;
    }

    return errorMessage;
  };
};

export const isAlpha = message => {
  const errorMessage = message || t`Use only alpha characters`;
  const nameRegex = new RegExp(/^[A-Za-z -]+$/);

  return value => errorHelper({value, errorMessage, nameRegex});
};

export const isAlphaNumeric = message => {
  const errorMessage =
    message || t`Use only alphanumeric characters, spaces and -./`;
  const nameRegex = new RegExp(/^[A-Za-z\d\s\-./]+$/);

  return value => errorHelper({value, errorMessage, nameRegex});
};

export const isAlphaExtraNumeric = message => {
  const errorMessage =
    message || t`Use only alphanumeric characters and/or: -()/,`;
  const nameRegex = new RegExp(/^[A-Za-z0-9 -()/,]+$/);

  return value => errorHelper({value, errorMessage, nameRegex});
};

export const isValidYear = message => {
  const currentYear = new Date().getFullYear();
  const errorMessage =
    message ||
    t`Invalid date, must be from ${currentYear - 100} until present.`;

  return value => {
    if (
      !(
        new Date(parseDate(value)) <= Date.now() &&
        new Date(parseDate(value)).getFullYear() >= currentYear - 100
      )
    ) {
      return errorMessage;
    }
  };
};

export const isValidDate = message => {
  const errorMessage =
    message || t`Invalid date. Must be in ${'ZZ.LL.AAAA'} format`;

  return value => {
    if (!isValid(new Date(parseDate(value)))) {
      return errorMessage;
    }
  };
};

export const isPhoneNumber = message => {
  const errorMessage = message || t`Invalid phone number`;
  const nameRegex = new RegExp(/^(\+\d{1,3}[- ]?|\d{3})?\d{10}$/);

  return value => errorHelper({value, errorMessage, nameRegex});
};

export const isEmail = message => {
  const errorMessage = message || t`Invalid email address`;

  const nameRegex = new RegExp(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );

  return value => errorHelper({value, errorMessage, nameRegex});
};

export const isNumber = message => {
  const errorMessage = message || t`Must be a number`;

  return value => {
    // Undefined is valid
    if (['undefined', 'object'].includes(typeof value) || value !== '') {
      return undefined;
    }

    // We don't allow numbers containing e
    return isNaN(value) || (value && value.length > 0 && value.includes('e'))
      ? errorMessage
      : undefined;
  };
};

export const isNumberWithTwoDecimals = message => {
  const min = 0;
  const errorMessage =
    message ||
    t`Enter a number of maximum 10 digits; including the two decimals that are optional.`;

  return value => {
    // Undefined is valid
    if (['undefined', 'object'].includes(typeof value) || value === '') {
      return undefined;
    }

    return value < 0
      ? t`Must be at least ${min}`
      : (value.toString().includes('.') && value.toString().length > 11) ||
          (!value.toString().includes('.') && value.toString().length > 10) ||
          !new RegExp(/^\d+(\.\d{0,2})?$/).test(value)
        ? errorMessage
        : undefined;
  };
};

export const hasMinVal = (min, message) => {
  const errorMessage = message || t`Must be at least ${min}`;

  return value => {
    // Undefined is valid
    if (['undefined', 'object'].includes(typeof value)) {
      return undefined;
    }

    return isNaN(value) || value >= min ? undefined : errorMessage;
  };
};

export const hasMaxVal = (max, message) => {
  const errorMessage = message || t`Must be at most ${max}`;

  return value => {
    // Undefined is valid
    if (['undefined', 'object'].includes(typeof value)) {
      return undefined;
    }

    return isNaN(value) || value <= max ? undefined : errorMessage;
  };
};

export const hasMinLength = (min, message) => {
  const errorMessage = message || t`Must be at least ${min} characters`;

  return value => {
    // Undefined is valid
    if (['undefined', 'object'].includes(typeof value)) {
      return undefined;
    }

    return String(value).length >= min ? undefined : errorMessage;
  };
};

export const hasMaxLength = (max, message) => {
  const errorMessage = message || t`Must be at most ${max} characters`;

  return value => {
    // Undefined is valid
    if (['undefined', 'object'].includes(typeof value)) {
      return undefined;
    }

    return String(value).length <= max ? undefined : errorMessage;
  };
};

export const isInRange = (min, max, message) => {
  const errorMessage = message || t`Must be in ${min}-${max} range`;

  return value => {
    // Undefined is valid
    if (['undefined', 'object'].includes(typeof value)) {
      return undefined;
    }

    return value >= min && value <= max ? undefined : errorMessage;
  };
};

export const isMultipleOf = (step, message) => value => {
  if (value === undefined || value === null) return undefined;
  const errorMessage =
    message || t`Sunt permise doar valori cu pasul de ${step}`;
  return Number(value) % step === 0 ? undefined : errorMessage;
};

export const isIBAN = message => {
  let errorMessage = message || t`Invalid IBAN`;

  return value => {
    // Undefined is valid
    if (!value) {
      return undefined;
    }

    // Return error message if value is not a string
    if (typeof value !== 'string') {
      return errorMessage;
    }

    const formatValue = value.trim().replace(/\s/g, '');

    // Return error message if value is not propper length
    // if (formatValue.length !== 24) {
    //   errorMessage = t`Invalid IBAN must have 24 characters`;
    //   return errorMessage;
    // }

    // Return error message if value is not propper IBAN
    if (!isValidIBAN(formatValue)) {
      errorMessage = t`Invalid IBAN for Romania`;
      return errorMessage;
    }

    return undefined;
  };
};

const CIF_CRC_TEST_KEY = '753217532';
const CIF_FISCAL_ATTRIBUTE = 'RO';

export const isCIF = message => {
  const errorMessage = message || t`Invalid fiscal code`;

  return value => {
    // Undefined is valid
    if (['undefined', 'object'].includes(typeof value)) {
      return undefined;
    }

    const isValueString = typeof value === 'string';
    const isValueInteger = Number.isInteger(value);

    // Return error message if value is not a string or integer
    if (!isValueString && !isValueInteger) {
      return errorMessage;
    }

    // If value is an integer convert to string
    if (isValueInteger) {
      value = value.toString();
    }

    // Remove spaces and transform to uppercase
    value = value.replace(/ /g, '').toUpperCase();

    // Check for tax attribute
    if (value.indexOf(CIF_FISCAL_ATTRIBUTE) === 0) {
      // Tax attribute is found, remove it
      value = value.slice(CIF_FISCAL_ATTRIBUTE.length);
    }

    // Return error message if value without tax attribute
    // has more than 10 characters
    if (value.length > 10 || +value === 0) {
      return errorMessage;
    }

    // Converting string to array
    value = value.split('');

    // Get CRC control number as integer and remove from array
    const controlNumber = parseInt(value.pop());

    // Reverse CRC test key
    const reversedCRCKey = CIF_CRC_TEST_KEY.split('').reverse();

    // Reverse identification number
    value = value.reverse();

    // Start validation

    // Multiply each character from the reversed identification number
    // with the corresponding number from the reversed CRC test key
    // and add it to a sum
    let multiplicationSum = 0;

    value.forEach((character, index) => {
      if (reversedCRCKey[index]) {
        multiplicationSum += character * reversedCRCKey[index];
      }
    });

    // Multiply the multiplication :) sum with 10
    multiplicationSum = multiplicationSum * 10;

    // Apply modulo 11 to the multiplication sum
    let modulo = multiplicationSum % 11;

    // Validate the CRC control
    // Modulo should be control number or
    // if modulo is 10 then control number should be 0
    if ((modulo === 10 && controlNumber === 0) || modulo === controlNumber) {
      return undefined;
    }

    return errorMessage;
  };
};

const ONRC_ENTITIES = [
  'J', // legal entity
  'F', // registered sole trader
  'C', // building society
];

export const isONRCRegistrationNumber = message => {
  const errorMessage = message || t`Invalid registration number`;

  return value => {
    // Undefined is valid
    if (['undefined', 'object'].includes(typeof value)) {
      return undefined;
    }

    const isValueString = typeof value === 'string';

    // Return error message if value is not a string or integer
    if (!isValueString) {
      return errorMessage;
    }

    // Remove spaces and transform to uppercase
    value = value.replace(/ /g, '').toUpperCase();

    // Validate entity attribute
    if (ONRC_ENTITIES.includes(value.charAt(0))) {
      // Entity attribute is found, remove it and
      // remove any potential whitespace left before the region number (SIRUTA)
      value = value
        // Hardcoded length for ONRC_ENTITIES
        .slice(1)
        .trim();
    } else {
      // Entity attribute isn't found, return error message
      return errorMessage;
    }

    // Split value by "/" in order to
    // get region number, order number and year
    const registrationDetails = value.split('/');

    // Check if region number, order number and year are all present
    if (registrationDetails.length !== 3) {
      return errorMessage;
    }

    const registrationDetailsNumbers = registrationDetails.map(
      registrationDetail => Number(registrationDetail),
    );

    const [regionNumber, orderNumber, year] = registrationDetailsNumbers;

    // Validate region number (SIRUTA format)
    // Valid values: range 1-40, 51 (Calarasi), 52 (Giurgiu)
    if (
      (regionNumber < 1 || regionNumber > 40) &&
      regionNumber !== 51 &&
      regionNumber !== 52
    ) {
      return errorMessage;
    }

    // Validate order number
    // It starts yearly with 1
    if (orderNumber < 1) {
      return errorMessage;
    }

    // Validate year
    // ONRC was founded in 1990
    if (year < 1990) {
      return errorMessage;
    }

    // Everything ok
    return undefined;
  };
};

// TODO: Needs documentation, inherited code
export const isCNP = message => {
  const errorMessage = message || t`SSN is invalid`;
  const foreignResidentMessage = t`Cetatenii straini nu sunt eligibili`;
  const ageLimitMessage = t`Clientul nu are 18 ani impliniti`;

  return value => {
    // Undefined is valid
    if (['undefined', 'object'].includes(typeof value)) {
      return undefined;
    }

    // Trim value
    value = value.trim();

    // Check for required number of characters
    if (value.length !== 13) {
      return errorMessage;
    }

    let hashResult = 0;
    let numberOfDays = undefined;
    const cnp = [];
    const hashTable = [2, 7, 9, 1, 4, 6, 3, 5, 8, 2, 7, 9];

    for (let i = 0; i < 13; i++) {
      cnp[i] = parseInt(value.charAt(i), 10);

      if (isNaN(cnp[i])) {
        return errorMessage;
      }

      if (i < 12) {
        hashResult = hashResult + cnp[i] * hashTable[i];
      }
    }

    hashResult = hashResult % 11;

    if (hashResult === 10) {
      hashResult = 1;
    }

    let year = cnp[1] * 10 + cnp[2];
    let month = parseInt(cnp[3] * 10) + parseInt(cnp[4]);
    let day = parseInt(cnp[5] * 10) + parseInt(cnp[6]);

    switch (cnp[0]) {
      case 1:
      case 2:
        year += 1900;
        break;
      case 3:
      case 4:
        year += 1800;
        break;
      case 5:
      case 6:
        year += 2000;
        break;
      case 7:
      case 8:
      case 9:
        return foreignResidentMessage;
      default:
        return errorMessage;
    }

    switch (month) {
      case 2:
        numberOfDays =
          (year % 4 === 0 && year % 100) || year % 400 === 0 ? 29 : 28;
        break;
      case 9:
      case 4:
      case 6:
      case 11:
        numberOfDays = 30;
        break;
      default:
        numberOfDays = 31;
        break;
    }

    if (
      year < 1800 ||
      year > 2099 ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > numberOfDays
    ) {
      return errorMessage;
    }

    const parsedDate = parse(
      `${year}/${month}/${day}`,
      'yyyy/MM/dd',
      new Date(),
    );
    const age = differenceInYears(new Date(), parsedDate);

    if (age < 18) {
      return ageLimitMessage;
    }

    return cnp[12] === hashResult ? undefined : errorMessage;
  };
};
